import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/ui/shadcn/breadcrumb';
import { mq } from './breakpoints';
import { BackLink, type BackLinkProps } from './components/BackLink/BackLink';
import styles from './PageHeading.module.css';
import { useMediaQuery } from './useMediaQuery';

/** One place above the page in its breadcrumb trail. */
export interface Crumb {
    readonly label: string;
    readonly to: string;
    /** The route state the link carries, as the back link to the same place does (a list's filters). */
    readonly state?: unknown;
}

export interface PageHeadingProps {
    /** The places above this page, outermost first (`Settings`, then `Users`). */
    readonly trail: readonly Crumb[];
    /** This page's own name, the trail's last item — usually the `h1`'s text. */
    readonly current: string;
    /** The arrow beside the title, one level up. */
    readonly back: BackLinkProps;
    /** The page's own `h1`, with its ref, focus handling and test id, and anything that sits on its line. */
    readonly children: ReactNode;
    readonly className?: string | undefined;
    /** Prefixes the breadcrumb's test ids: `<id>-breadcrumb`, `<id>-crumb-<index>`. */
    readonly 'data-testid'?: string;
}

/**
 * The top of a page below a section's list (design-system §6, Back navigation): the shadcn Breadcrumb naming
 * every place above it, then the title row — the back arrow (`BackLink`) and the page's `h1` side by side.
 *
 * The page keeps its own `h1`, passed as children, so its ref, `tabIndex` and focus-on-arrival stay where they
 * are. The breadcrumb's places are links and the page itself is the last item, `aria-current="page"`; at the
 * 320px reflow the trail wraps and a long name breaks rather than scrolling sideways (design-system §9).
 *
 * The arrow sits in a box one title line tall. From `bp/tablet` up the box is centred on the title line, which
 * holds the title and any actions beside it. Below it the title wraps and the actions drop under it, so the box
 * sits at the row's top, level with the title's first line (`data-layout` from `matchMedia(mq.tablet)`, never a
 * raw width in `@media`). The arrow is centred in its 28px square, and the trail is inset by the space beside it so
 * its first letter lines up with the arrow. Every alignment is layout, never a margin offset.
 */
export function PageHeading({
    trail,
    current,
    back,
    children,
    className,
    'data-testid': testId = 'page',
}: PageHeadingProps) {
    const designed = useMediaQuery(mq.tablet);
    return (
        <div className={cn(styles.heading, className)}>
            <Breadcrumb data-testid={`${testId}-breadcrumb`}>
                <BreadcrumbList className={styles.trail}>
                    {trail.map((crumb, index) => (
                        <Fragment key={`${crumb.to}-${crumb.label}`}>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild className={styles.crumb}>
                                    <Link
                                        data-testid={`${testId}-crumb-${index}`}
                                        state={crumb.state}
                                        to={crumb.to}
                                    >
                                        {crumb.label}
                                    </Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator className={styles.separator} />
                        </Fragment>
                    ))}
                    <BreadcrumbItem>
                        <BreadcrumbPage className={styles.current}>{current}</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <div className={styles.titleRow} data-layout={designed ? 'centred' : 'first-line'}>
                <div className={styles.arrowBox} data-testid={`${testId}-arrow-box`}>
                    <BackLink {...back} />
                </div>
                <div className={styles.title}>{children}</div>
            </div>
        </div>
    );
}
